import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, DiamondIcon } from './icons/Icons';
import { useAuthStore } from '../stores/useAuthStore';
import { useAuthModalStore } from '../stores/useAuthModalStore';
import { useNotificationStore } from '../stores/useNotificationStore';
import { useFeedbackModalStore } from '../stores/useFeedbackModalStore';
import { BillingService, SubscriptionService } from '../services/billingService';
import { APP_HOME } from '../utils/segment';

interface UserProfileProps {
    onOpenUpgradeModal: () => void;
    /**
     * Suporte & Feedback e Notificações dentro do menu da conta (menu do
     * Marketing, que não tem esses itens soltos na lateral).
     */
    withSupportItems?: boolean;
}

// Minimum popover width — enough to fit "Restam 9.999 créditos" + plan label on
// one row with the compact p-3 layout. Actual width matches the trigger so the
// popover fits inside the sidebar (no overflow into main content).
const POPOVER_MIN_WIDTH = 232;

interface SidebarPopoverProps {
    isOpen: boolean;
    triggerRef: React.RefObject<HTMLButtonElement | null>;
    popoverRef: React.RefObject<HTMLDivElement | null>;
    children: React.ReactNode;
}

// Renders a popover anchored to a sidebar trigger via React Portal + position:fixed.
// Portal escapes the sidebar's overflow-y-auto clipping context; position is
// recomputed on resize/scroll so the popover follows the trigger if the layout
// shifts. Width matches the trigger so the popover stays inside the sidebar.
const SidebarPopover: React.FC<SidebarPopoverProps> = ({ isOpen, triggerRef, popoverRef, children }) => {
    const [pos, setPos] = useState<{ bottom: number; left: number; width: number } | null>(null);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const width = Math.max(POPOVER_MIN_WIDTH, rect.width);
        const viewportWidth = window.innerWidth;
        const left = Math.min(rect.left, viewportWidth - width - 8);
        setPos({
            bottom: window.innerHeight - rect.top + 8,
            left: Math.max(8, left),
            width,
        });
    }, [triggerRef]);

    useEffect(() => {
        if (!isOpen) {
            setPos(null);
            return;
        }
        updatePosition();
        // Capture-phase scroll catches scroll on any ancestor (e.g. the sidebar's
        // overflow-y-auto container) so the popover follows the trigger.
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    if (!isOpen || !pos) return null;
    return createPortal(
        <div
            ref={popoverRef}
            style={{
                position: 'fixed',
                bottom: `${pos.bottom}px`,
                left: `${pos.left}px`,
                width: `${pos.width}px`,
            }}
            className="bg-snapfy-surface-hover rounded-lg shadow-lg p-3 text-snapfy-text-primary z-50"
        >
            {children}
        </div>,
        document.body,
    );
};

const UserProfile: React.FC<UserProfileProps> = ({ onOpenUpgradeModal, withSupportItems = false }) => {
    const { t } = useTranslation('common');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [credits, setCredits] = useState<number | null>(null);
    const [planName, setPlanName] = useState<string>('Free');
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { user, isAuthenticated, logout } = useAuthStore();
    const { openModal } = useAuthModalStore();
    const { reset: resetNotifications, unreadCount } = useNotificationStore();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const insideTrigger = triggerRef.current?.contains(target);
            const insidePopover = popoverRef.current?.contains(target);
            if (!insideTrigger && !insidePopover) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const fetchCredits = async () => {
            if (!isAuthenticated) {
                setCredits(null);
                return;
            }
            try {
                const [balance, subscription] = await Promise.all([
                    BillingService.getBalance(),
                    SubscriptionService.getCurrentSubscription(),
                ]);
                setCredits(balance);
                if (subscription.hasSubscription && subscription.subscription) {
                    setPlanName(subscription.subscription.plan.name);
                } else {
                    setPlanName('Free');
                }
            } catch (error) {
                console.error('Failed to fetch credits:', error);
            }
        };

        if (isMenuOpen && isAuthenticated) {
            fetchCredits();
        }
    }, [isMenuOpen, isAuthenticated]);

    const handleLogout = async () => {
        setIsMenuOpen(false);
        setCredits(null);
        resetNotifications();
        await logout();
        navigate('/');
    };

    const handleLogin = () => {
        setIsMenuOpen(false);
        openModal({ redirectPath: APP_HOME });
    };

    if (!isAuthenticated) {
        return (
            <div className="relative w-full">
                <button
                    ref={triggerRef}
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    aria-haspopup="menu"
                    aria-expanded={isMenuOpen}
                    aria-label={t('sidebar.openUserMenu')}
                    className="flex items-center w-full p-2 rounded-xl hover:bg-snapfy-inner transition-colors text-left"
                >
                    <div className="w-10 h-10 rounded-full bg-snapfy-inner flex items-center justify-center">
                        <svg className="w-6 h-6 text-snapfy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    </div>
                    <div className="hidden lg:block ml-3 flex-1">
                        <p className="font-semibold text-snapfy-text-primary">{t('sidebar.visitor')}</p>
                        <p className="text-sm text-snapfy-text-secondary">{t('sidebar.doLogin')}</p>
                    </div>
                    <ChevronDownIcon className="hidden lg:block w-5 h-5 text-snapfy-text-secondary ml-auto" />
                </button>

                <SidebarPopover isOpen={isMenuOpen} triggerRef={triggerRef} popoverRef={popoverRef}>
                    <div className="flex items-center mb-4">
                        <div className="w-10 h-10 rounded-full bg-snapfy-inner flex items-center justify-center">
                            <svg className="w-6 h-6 text-snapfy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <div className="ml-3 overflow-hidden">
                            <p className="font-semibold">{t('sidebar.visitor')}</p>
                            <p className="text-sm text-snapfy-text-secondary">{t('sidebar.notLoggedIn')}</p>
                        </div>
                    </div>

                    <button
                        onClick={handleLogin}
                        className="w-full snapfy-gradient text-white text-sm font-semibold py-2.5 px-2 rounded-lg flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity mb-4 whitespace-nowrap"
                    >
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                        </svg>
                        {t('sidebar.loginOrSignUp')}
                    </button>

                    <div className="space-y-2 text-sm text-snapfy-text-secondary">
                        <Link to="/help" onClick={() => setIsMenuOpen(false)} className="block w-full text-left p-2 rounded hover:bg-snapfy-inner">{t('sidebar.help')}</Link>
                    </div>

                    <div className="border-t border-snapfy-border mt-4 pt-4 flex items-center justify-between text-xs text-snapfy-text-secondary">
                        <Link to="/terms" onClick={() => setIsMenuOpen(false)} className="hover:text-snapfy-text-secondary">{t('sidebar.terms')}</Link>
                        <Link to="/privacy" onClick={() => setIsMenuOpen(false)} className="hover:text-snapfy-text-secondary">{t('sidebar.privacy')}</Link>
                    </div>
                </SidebarPopover>
            </div>
        );
    }

    const userName = user?.name || 'Usuario';
    const userEmail = user?.email || '';
    const userAvatar = user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=random`;

    const creditsLeft = credits !== null ? credits : 0;
    const totalCredits = 1000;
    const progressPercentage = Math.min((creditsLeft / totalCredits) * 100, 100);

    return (
        <div className="relative w-full">
            <button
                ref={triggerRef}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                aria-label={t('sidebar.openUserMenu')}
                className="flex items-center w-full p-2 rounded-xl hover:bg-snapfy-inner transition-colors text-left"
            >
                <img
                    src={userAvatar}
                    alt={t('sidebar.userAvatarAlt')}
                    className="w-10 h-10 rounded-full object-cover"
                />
                <div className="hidden lg:block ml-3 flex-1">
                    <p className="font-semibold text-snapfy-text-primary truncate max-w-[120px]">{userName}</p>
                    <p className="text-sm text-snapfy-text-secondary">{planName} plan</p>
                </div>
                <ChevronDownIcon className="hidden lg:block w-5 h-5 text-snapfy-text-secondary ml-auto" />
            </button>

            <SidebarPopover isOpen={isMenuOpen} triggerRef={triggerRef} popoverRef={popoverRef}>
                <div className="flex items-center mb-4">
                    <img
                        src={userAvatar}
                        alt={t('sidebar.userAvatarAlt')}
                        className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="ml-3 overflow-hidden">
                        <p className="font-semibold truncate">{userName}</p>
                        <p className="text-sm text-snapfy-text-secondary truncate">{userEmail}</p>
                    </div>
                </div>

                {user && !user.emailVerifiedAt ? (
                    <div className="mb-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                        <p className="text-yellow-400 font-semibold text-sm">{t('sidebar.activateAccount')}</p>
                        <p className="text-snapfy-text-secondary text-xs mt-1">
                            {t('sidebar.verifyEmailForCredits')}
                        </p>
                        <button
                            onClick={() => { navigate('/verify-email'); setIsMenuOpen(false); }}
                            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-4 rounded-lg mt-2 text-sm"
                        >
                            {t('sidebar.verifyEmail')}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="mb-4">
                            <div className="flex justify-between items-baseline text-xs mb-1 gap-2">
                                <span className="text-snapfy-text-secondary truncate">{planName}</span>
                                <span className="font-semibold whitespace-nowrap">{t('sidebar.remainingCreditsCompact', { count: creditsLeft })}</span>
                            </div>
                            <div className="w-full bg-snapfy-inner rounded-full h-2">
                                <div className="bg-[#7063f7] h-2 rounded-full" style={{ width: `${progressPercentage}%` }}></div>
                            </div>
                        </div>

                        <button
                            onClick={() => { onOpenUpgradeModal(); setIsMenuOpen(false); }}
                            className="w-full bg-snapfy-primary text-snapfy-text-primary text-sm font-semibold py-2 px-2 rounded-lg flex items-center justify-center gap-1.5 hover:opacity-90 transition-colors mb-4 whitespace-nowrap"
                        >
                            <DiamondIcon />
                            {t('sidebar.buyCreditsAction')}
                        </button>
                    </>
                )}

                <div className="space-y-2 text-sm text-snapfy-text-secondary">
                    <Link to="/profile" onClick={() => setIsMenuOpen(false)} className="block w-full text-left p-2 rounded hover:bg-snapfy-inner">{t('sidebar.viewProfile')}</Link>
                    <Link to="/help" onClick={() => setIsMenuOpen(false)} className="block w-full text-left p-2 rounded hover:bg-snapfy-inner">{t('sidebar.help')}</Link>
                    {withSupportItems && (
                        <>
                            <button
                                onClick={() => { setIsMenuOpen(false); useFeedbackModalStore.getState().openModal(); }}
                                className="block w-full text-left p-2 rounded hover:bg-snapfy-inner"
                            >
                                {t('sidebar.supportFeedback')}
                            </button>
                            {/* O Layout abre a lista de notificações (o mesmo "Ver todas" do Product Studio). */}
                            <button
                                onClick={() => { setIsMenuOpen(false); window.dispatchEvent(new Event('open-notifications')); }}
                                className="flex items-center justify-between gap-2 w-full text-left p-2 rounded hover:bg-snapfy-inner"
                            >
                                {t('sidebar.notifications')}
                                {unreadCount > 0 && (
                                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-snapfy-primary text-white text-[10px] font-bold flex items-center justify-center">
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                )}
                            </button>
                        </>
                    )}
                    <button className="block w-full text-left p-2 rounded hover:bg-snapfy-inner">{t('sidebar.deleteAccount')}</button>
                    <button className="block w-full text-left p-2 rounded hover:bg-snapfy-inner">{t('sidebar.api')}</button>
                    <button onClick={handleLogout} className="block w-full text-left p-2 rounded hover:bg-snapfy-inner text-red-400">{t('sidebar.logout')}</button>
                </div>

                <div className="border-t border-snapfy-border mt-4 pt-4 flex items-center justify-between text-xs text-snapfy-text-secondary">
                    <Link to="/terms" onClick={() => setIsMenuOpen(false)} className="hover:text-snapfy-text-secondary">{t('sidebar.terms')}</Link>
                    <Link to="/privacy" onClick={() => setIsMenuOpen(false)} className="hover:text-snapfy-text-secondary">{t('sidebar.privacy')}</Link>
                </div>
            </SidebarPopover>
        </div>
    );
};

export default UserProfile;
